/**
 * Astor Predicted Docs DTO — predicts which documents a borrower will need
 * NEXT on a given case.
 *
 * Distinct from `consumer.documentRequirements`, which lists the static
 * required-doc checklist for a loan_application entity. Predicted-docs
 * surfaces probabilistic next-docs based on case type, lifecycle stage, and
 * already-uploaded attachments. Renders in the consumer Documents page
 * "Astor predicts" mint-bordered panel with per-row pre-stage CTAs.
 *
 * Generated via deterministic projection from existing gRPC reads
 * (case + attachments + document-checklist) in `8-agent-service`. The
 * confidence score is a heuristic computed from data completeness; a
 * future Astor twin dispatch can swap the generator without UI churn.
 */

import { z } from 'zod';

export const AstorPredictedDocSchema = z.object({
  /** Stable id (uuid v4) — keyed React rendering. */
  id: z.string().uuid(),
  /** Human-readable document name (e.g. "2025 Schedule E"). */
  name: z.string().min(1),
  /**
   * Document type / category — drives the icon and the pre-stage flow's
   * checklist mapping. Snake.dot.case canonical key
   * (e.g. `tax_return.schedule_e`, `bank_statement.business`,
   * `appraisal.property`).
   */
  documentType: z.string().min(1),
  /**
   * 0-100 confidence percentage. Surfaces as the small grey "85%" pill on
   * the row. Computed from the heuristic ladder (case-type → standard list
   * gives ≥90, prior-cadence match gives 60-85, weak signal gives ≤60).
   */
  confidence: z.number().min(0).max(100),
  /**
   * Plain-English reason ("Based on your last filing cadence", "Standard
   * for jumbo refinance applications"). Rendered as the row subtitle.
   */
  reason: z.string().min(1),
  /**
   * Optional deep-link the pre-stage CTA can route to (e.g. an upload
   * dialog with this doc-type pre-selected).
   */
  preStageHref: z.string().optional(),
});
export type AstorPredictedDoc = z.infer<typeof AstorPredictedDocSchema>;

export const AstorPredictedDocsSchema = z.object({
  /** Echoes the requested case_id. */
  caseId: z.string().uuid(),
  /** 0-N predicted docs. Sorted descending by confidence. */
  predictions: z.array(AstorPredictedDocSchema),
  /** ISO-8601 timestamp when the projection was generated. */
  generatedAt: z.string().datetime(),
});
export type AstorPredictedDocsDto = z.infer<typeof AstorPredictedDocsSchema>;
